import React, { useCallback, useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import {
  Card,
  CardBody,
  Typography,
  Checkbox,
  Select,
  Option,
  Button,
  Input,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import { setSessionExpired, useMainController } from "../../context";
import { useMutation } from "react-query";
import http from "../../services/http-common";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  zoomPlugin
);

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  .toISOString()
  .slice(0, 10);
const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  .toISOString()
  .slice(0, 10);

const MyChart = ({ isMoodTrackingEnabled = true }) => {
  const dispatch = useMainController();
  const chartRef = useRef();
  const [showMood, setShowMood] = useState(true);
  const [showDoses, setShowDoses] = useState(true);
  const [selectedCompound, setSelectedCompound] = useState("all");
  const [snacks, setSnacks] = useState([]);
  const [aiJournalData, setAiJournalData] = useState(null);
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [selectedPoint, setSelectedPoint] = useState(null);
  // Utility to get all dates in range
  function getAllDatesInRange(start, end) {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().slice(0, 10));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  const allDates = getAllDatesInRange(startDate, endDate);

  const createSnack = useCallback((message, variant) => {
    const id = Date.now();
    const newSnack = { id, message, variant, visible: true };
    setSnacks([newSnack]);

    setTimeout(() => {
      setSnacks((prevSnacks) =>
        prevSnacks?.map((snack) =>
          snack.id === id ? { ...snack, visible: false } : snack
        )
      );
    }, 2500);

    setTimeout(() => {
      setSnacks((prevSnacks) => prevSnacks.filter((snack) => snack.id !== id));
    }, 3000);
  }, []);
  // Prepare mood data for chart
  const moodData = aiJournalData?.map((d) => ({
    date: d.created_at,
    score: d.metadata.affectVec.composite_score,
    summary: d.metadata.affectVec.summary,
    dominant_emotion: d.metadata.affectVec.dominant_emotion,
  }));

  // Prepare dose data for chart
  const doseData = aiJournalData?.flatMap((d) =>
    (d.metadata.medications.meds_taken || [])?.map((med) => ({
      compound: med.substance,
      amount: med.dose,
      timestamp: d.created_at,
      location: "N/A",
      summary: d.summary,
    }))
  );

  const filteredDoseData =
    selectedCompound === "all"
      ? doseData
      : doseData.filter((d) => d.compound === selectedCompound);

  const moodLineData = {
    labels: moodData?.map((d) => d.date),
    datasets: [
      {
        label: "Mood Score",
        data: moodData?.map((d) => ({
          x: d.date.slice(0, 10),
          y: d.score,
          summary: d.summary,
          dominant_emotion: d.dominant_emotion,
        })),
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.2)",
        fill: false,
        pointRadius: 5,
        hidden: !showMood,
        spanGaps: true,
      },
    ],
  };
  const doseMarkers = {
    labels: allDates,
    datasets: [
      {
        label: "Dose Events",
        data: filteredDoseData?.map((d) => ({
          x: d.timestamp.slice(0, 10),
          y: 0,
          details: d,
        })),
        borderColor: "rgba(242, 147, 167,1)",
        backgroundColor: "rgba(242, 147, 167,0.2)",
        pointBackgroundColor: "rgba(255,99,132,0.5)",
        showLine: false,
        pointRadius: 5,
        hidden: !showDoses,
      },
    ],
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        type: "time",
        time: { unit: "day" },
        title: { display: true, text: "Date" },
      },
      y: {
        title: { display: true, text: "Mood Score" },
        min: -2,
        max: 2,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataPoint = context.raw.details;
            // Dose event
            if (dataPoint) {
              return `Substance: ${dataPoint.compound}, Amount: ${dataPoint.amount}`;
            }
            // Mood point
            const { dominant_emotion, summary } = context.raw;
            // Split summary into lines of max 50 chars for responsiveness
            const wrapText = (text, maxLen = 50) => {
              if (!text) return "";
              const words = text.split(" ");
              let lines = [];
              let currentLine = "";
              words.forEach((word) => {
                if ((currentLine + word).length > maxLen) {
                  lines.push(currentLine.trim());
                  currentLine = "";
                }
                currentLine += word + " ";
              });
              if (currentLine) lines.push(currentLine.trim());
              return lines;
            };
            return [
              `Dominant Emotion: ${dominant_emotion}`,
              "Summary:",
              ...wrapText(summary),
            ];
          },
        },
        bodyFont: {
          size: 13,
        },
        bodySpacing: 4,
        displayColors: false,
      },
      zoom: {
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
        pan: {
          enabled: true,
          mode: "x",
        },
      },
    },
  };
  const combinedData = {
    labels: allDates,
    datasets: [
      ...(showMood && isMoodTrackingEnabled ? moodLineData.datasets : []),
      ...(showDoses ? doseMarkers.datasets : []),
    ],
  };

  const getAiJournalData = useMutation(
    ({ from, to }) => {
      return http.post(
        "chat/get-summaries-journal",
        {
          from,
          to,
          pageNumber: 1,
          pageSize: 30,
        },
        setSessionExpired,
        dispatch
      );
    },
    {
      onSuccess: ({ data }) => {
        setAiJournalData(data.summaries);
      },
      onError: (error) => {
        switch (error.response.status) {
          case 500: {
            toast.error("One problem is on server. Please try again later.");
            break;
          }
          case 404: {
            toast.error("Please check network status. And then try again.");
            break;
          }
          default:
            toast.error(error.message);
        }
      },
    }
  );

  useEffect(() => {
    // Only call if both dates are set
    if (startDate && endDate) {
      getAiJournalData.mutate({
        from: new Date(startDate).toISOString(),
        to: new Date(endDate).toISOString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const selectedData = React.useMemo(() => {
    if (!selectedPoint || !aiJournalData) return [];
    const selectedDate =
      typeof selectedPoint.x === "string"
        ? selectedPoint.x.slice(0, 10)
        : new Date(selectedPoint.x).toISOString().slice(0, 10);
    return aiJournalData.find(
      (d) =>
        d.created_at &&
        d.created_at.slice(0, 10) === selectedDate &&
        d.metadata?.affectVec?.composite_score === selectedPoint.y &&
        d.metadata?.affectVec?.dominant_emotion ===
          selectedPoint.dominant_emotion &&
        d.metadata?.affectVec?.summary === selectedPoint.summary
    );
  }, [selectedPoint, aiJournalData]);

  return (
    <Card className="p-2 sm:p-4 shadow-none w-full max-w-4xl mx-auto">
      <CardBody className="sm:!p-6 !p-0">
        <div>
          {snacks?.map((snack) => (
            <div
              key={snack.id}
              className={`${
                snack.visible ? "opacity-100" : "translate-y-10 opacity-0"
              } absolute bottom-4 left-4 z-50 flex items-center space-x-4 divide-x divide-slate-200 rounded-xl bg-white p-4 pr-5 text-slate-500 shadow transition-all duration-500 ease-in-out`}
              role="alert"
            >
              <div className="max-w-md text-ellipsis ps-4 font-normal ">
                {snack.message}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 mb-4 items-center"></div>
        <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:gap-4">
          <Input
            type="date"
            label="Start Date"
            className="w-full "
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            type="date"
            label="End Date"
            className="w-full "
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <div className="w-full sm:w-auto">
            <Select
              label="Filter by Dose"
              value={selectedCompound}
              onChange={(val) => setSelectedCompound(val)}
              className="w-full"
            >
              <Option value="all">All</Option>
              {[...new Set(doseData?.map((d) => d.compound))]?.map(
                (compound) => (
                  <Option key={compound} value={compound}>
                    {compound}
                  </Option>
                )
              )}
            </Select>
          </div>{" "}
        </div>
        <div className="w-full overflow-x-auto">
          <div className="min-w-[320px] max-w-full" style={{ height: "350px" }}>
            <Line
              ref={chartRef}
              data={combinedData}
              options={options}
              onClick={(event) => {
                const chart = chartRef.current;
                if (!chart) return;
                const points = chart.getElementsAtEventForMode(
                  event.nativeEvent,
                  "nearest",
                  { intersect: true },
                  false
                );
                if (points.length > 0) {
                  const { datasetIndex, index } = points[0];
                  const dataset = combinedData.datasets[datasetIndex];
                  const dataPoint = dataset.data[index];
                  if (dataset.label === "Mood Score" && dataPoint.y !== null) {
                    setSelectedPoint(dataPoint);
                  } else {
                    setSelectedPoint(null);
                  }
                } else {
                  setSelectedPoint(null);
                }
              }}
            />
          </div>
        </div>
        {selectedPoint && (
          <div className="w-full flex flex-col sm:flex-row gap-4 mt-4">
            <div className="flex-1 bg-blue-50 rounded shadow p-4">
              <h4 className="font-bold text-blue-700 mb-2">Mood Details</h4>
              <div className="flex sm:flex-row flex-col sm:justify-between items-center mb-2">
                <div>
                  <strong>Date:</strong> {selectedPoint.x}
                </div>
                <div>
                  <strong>Score:</strong> {selectedPoint.y}
                </div>
              </div>
              <div>
                <strong>Dominant Emotion:</strong>{" "}
                {selectedPoint.dominant_emotion}
              </div>
              <div className=" bg-blue-50  ">
                <h4 className="font-bold text-blue-700 mb-2">Summary</h4>
                <div className="text-gray-700">{selectedPoint.summary}</div>
              </div>{" "}
            </div>
          </div>
        )}

        {selectedPoint && selectedData && (
          <>
            <div className="w-full flex flex-col sm:flex-row gap-4 mt-4">
              {selectedData?.metadata?.botInterAction?.summary && (
                <div className="flex-1 bg-green-50 rounded shadow p-4 mb-4 sm:mb-0">
                  <h4 className="font-bold text-green-700 mb-2">
                    Bot Interaction
                  </h4>
                  <div className="text-gray-700 sm:text-base text-sm">
                    {selectedData.metadata.botInterAction.summary}
                  </div>
                </div>
              )}

              {selectedData?.metadata?.lifestyle?.lifestyle && (
                <div className="flex-1 bg-yellow-50 rounded shadow p-4 mb-4 sm:mb-0">
                  <h4 className="font-bold text-yellow-700 mb-2">Lifestyle</h4>
                  <div className="text-gray-700 sm:text-base text-sm">
                    <div>
                      <strong>Diet:</strong>{" "}
                      {selectedData.metadata.lifestyle.lifestyle.diet}
                    </div>
                    <div>
                      <strong>Sleep:</strong>{" "}
                      {selectedData.metadata.lifestyle.lifestyle.sleep}
                    </div>
                    <div>
                      <strong>Exercise:</strong>{" "}
                      {selectedData.metadata.lifestyle.lifestyle.exercise}
                    </div>
                    <div>
                      <strong>Hobbies:</strong>{" "}
                      {Array.isArray(
                        selectedData.metadata.lifestyle.lifestyle.hobbies
                      )
                        ? selectedData.metadata.lifestyle.lifestyle.hobbies.join(
                            ", "
                          )
                        : selectedData.metadata.lifestyle.lifestyle.hobbies}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {selectedData?.metadata?.topicsSummary?.topics?.length > 0 && (
              <div className="flex-1 bg-purple-50 rounded shadow mt-4  p-4">
                <h4 className="font-bold text-purple-700 mb-2">
                  Topics Discussed
                </h4>
                <ul className="text-gray-700 sm:text-base text-sm list-disc pl-4">
                  {selectedData.metadata.topicsSummary.topics.map(
                    (topic, idx) => (
                      <li key={idx}>
                        <strong>{topic.topic}</strong> ({topic.sentiment}):{" "}
                        {topic.discussion}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default MyChart;
